// Configuring the instance from the product. spec/03 §11a, spec/04 §8c.
//
// Object storage, outbound mail and the timestamp authorities are settings, not
// deployment configuration: an administrator changes them here and the next
// request uses them, with nobody touching a server.
//
// Two rules from the spec shape the whole screen.
//
// A secret is write-only (§11a rule 2). A stored credential comes back as
// `hasSecret: true` and never as a value, so every secret field here is blank
// even when one is configured, and leaving it blank keeps what is stored. There
// is no reveal, because a credential that can be read out of a settings screen
// is a credential that leaks through a screenshot or a support session.
//
// Testing is separate from saving (§8c rule 3), and tests the candidate rather
// than the stored setting — the alternative is discovering the credentials are
// wrong at the moment a signer clicks Sign.

import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import {
  ApiError,
  logout,
  readSetting,
  testSetting,
  writeSetting,
  readSigningStatus,
  type SettingKey,
  type SettingView,
  type SigningStatus,
} from './api.ts';
import { clearSession, readSession } from './session.ts';
import { regionFromEndpoint } from './storage-endpoint.ts';

type Outcome = { kind: 'ok'; message: string } | { kind: 'bad'; message: string } | null;

const KEYS: readonly SettingKey[] = ['storage', 'mail', 'timestamping'];

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Something between this page and the API answered instead.';
}

export function AdminPage(): ReactElement {
  const session = readSession();
  const [views, setViews] = useState<Partial<Record<SettingKey, SettingView>>>({});
  const [loading, setLoading] = useState(true);
  const [loadFailure, setLoadFailure] = useState<string | null>(null);

  const token = session?.token ?? '';

  const refresh = useCallback(async () => {
    const loaded: Partial<Record<SettingKey, SettingView>> = {};
    for (const key of KEYS) loaded[key] = await readSetting(token, key);
    setViews(loaded);
  }, [token]);

  useEffect(() => {
    if (session === null) {
      window.location.assign('/login');
      return;
    }
    refresh()
      .catch((error: unknown) => setLoadFailure(messageOf(error)))
      .finally(() => setLoading(false));
    // `session` is read once per mount on purpose; a token that changed under
    // an open screen is a sign-in that happened in another tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  if (session === null) return <main className="wrap" />;

  if (loading) {
    return (
      <main className="wrap">
        <h1>Settings</h1>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (loadFailure !== null) {
    return (
      <main className="wrap">
        <h1>Settings</h1>
        <div className="error" role="alert">
          <p>{loadFailure}</p>
          <p className="muted">
            If that says the session is not valid, <a href="/login">sign in again</a>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="wrap">
      <h1>Settings</h1>
      <p className="muted">
        Signed in as {session.email} · {session.organisationName} ·{' '}
        <a href="/envelopes">Envelopes</a>{' '}
        <button
          type="button"
          className="linklike"
          onClick={() => {
            void logout(token).catch(() => undefined);
            clearSession();
            window.location.assign('/login');
          }}
        >
          Sign out
        </button>
      </p>

      <SigningSection token={token} />
      <StorageSection token={token} view={views.storage} onSaved={refresh} />
      <MailSection token={token} view={views.mail} onSaved={refresh} />
      <TimestampingSection token={token} view={views.timestamping} onSaved={refresh} />
    </main>
  );
}

/** Shared chrome: the heading, the stored-state line, and the result banner. */
function Section({
  id,
  title,
  view,
  children,
  note,
}: {
  id: SettingKey;
  title: string;
  view: SettingView | undefined;
  children: ReactElement;
  note?: string;
}): ReactElement {
  return (
    <section className="setting" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`}>{title}</h2>
      <p className="muted">
        {view?.configured === true
          ? `Configured${view.updatedAt === null ? '' : `, last changed ${new Date(view.updatedAt).toLocaleString()}`}.`
          : 'Not configured yet.'}
        {view?.hasSecret === true && ' A credential is stored and is kept unless you replace it.'}
      </p>
      {note !== undefined && <p className="muted">{note}</p>}
      {children}
    </section>
  );
}

function Result({ outcome }: { outcome: Outcome }): ReactElement | null {
  if (outcome === null) return null;
  return (
    <div className={outcome.kind === 'ok' ? 'notice' : 'error'} role="alert">
      <p>{outcome.message}</p>
    </div>
  );
}

/**
 * One submit handler for both buttons.
 *
 * Which button was pressed decides whether this tests or saves, so the form is
 * read once and the two paths cannot drift into disagreeing about what the
 * candidate configuration is.
 */
function useSettingForm(
  token: string,
  key: SettingKey,
  build: (form: FormData) => Record<string, unknown>,
  onSaved: () => Promise<void>,
) {
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [busy, setBusy] = useState(false);

  const handle = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const action = (event.nativeEvent as SubmitEvent).submitter?.getAttribute('value') ?? 'save';
    const body = build(new FormData(event.currentTarget));

    setOutcome(null);
    setBusy(true);
    try {
      if (action === 'test') {
        const result = await testSetting(token, key, body);
        setOutcome({ kind: 'ok', message: result.detail });
      } else {
        await writeSetting(token, key, body);
        // The write is what succeeded or failed. Re-reading the section
        // afterwards is a convenience, and it used to sit inside this try —
        // so a settings read that failed for its own reasons reported a
        // configuration that *was* saved as one that was not, which sends an
        // administrator to type it all again.
        setOutcome({ kind: 'ok', message: 'Saved. The next request uses it.' });
        await onSaved().catch(() => undefined);
      }
    } catch (error) {
      setOutcome({ kind: 'bad', message: messageOf(error) });
    } finally {
      setBusy(false);
    }
  };

  return { outcome, busy, handle };
}

function Buttons({ busy }: { busy: boolean }): ReactElement {
  return (
    <div className="actions">
      <button type="submit" name="action" value="test" disabled={busy}>
        Test
      </button>
      <button type="submit" name="action" value="save" disabled={busy}>
        Save
      </button>
    </div>
  );
}

const text = (form: FormData, field: string) => String(form.get(field) ?? '').trim();

/**
 * A secret the administrator chose to replace, or nothing.
 *
 * Reads the form rather than trusting the input to exist: when a credential is
 * stored and nobody asked to change it, `Credential` renders no input at all,
 * so there is nothing here to read and nothing gets sent.
 */
const secret = (form: FormData, field: string) => {
  const value = String(form.get(field) ?? '');
  return value === '' ? {} : { [field]: value };
};

/**
 * Says whether a field currently holds a value, without showing a secret.
 *
 * Per field rather than per section. "A credential is stored" at the top of a
 * form with five boxes does not say *which* box it is about, and the one
 * question an administrator has in front of a half-filled form is which parts
 * of it are already real.
 */
function SetMark({ set, unsetLabel }: { set: boolean; unsetLabel?: string }): ReactElement {
  return set ? (
    <span className="set-mark is-set">Set</span>
  ) : (
    <span className="set-mark">{unsetLabel ?? 'Not set'}</span>
  );
}

/**
 * A stored credential, and the only way to replace one.
 *
 * When something is stored this renders **no input** until the administrator
 * asks for one. That is the whole design, and it is not about tidiness:
 *
 * - A password input that exists on the page gets filled by the browser's
 *   password manager, whatever `autocomplete` says. The previous version of
 *   this form rendered two of them, empty, and Chrome filled them with
 *   remembered values — so pressing Save sent a credential nobody typed and
 *   overwrote the one that was working. An input that does not exist cannot
 *   be autofilled and cannot be submitted.
 * - It makes "replace this" a decision with a click behind it, which is the
 *   right weight for the one action on this page that cannot be undone: the
 *   old value is not shown anywhere, so a wrong overwrite is unrecoverable.
 */
function Credential({
  stored,
  legend,
  children,
}: {
  stored: boolean;
  legend: string;
  children: ReactElement;
}): ReactElement {
  const [replacing, setReplacing] = useState(!stored);

  if (stored && !replacing) {
    return (
      <p>
        <span className="label">{legend}</span>
        <span className="stored-credential">
          <SetMark set />
          <span className="muted">Stored, and never shown again.</span>
          <button type="button" className="link" onClick={() => setReplacing(true)}>
            Replace
          </button>
        </span>
      </p>
    );
  }

  return (
    <>
      {children}
      {stored && (
        <p>
          <button type="button" className="link" onClick={() => setReplacing(false)}>
            Keep the stored credential instead
          </button>
        </p>
      )}
    </>
  );
}

/**
 * The fields of one section, and which of them somebody actually changed.
 *
 * A save sends the stored value with the changed fields laid over it, so a
 * field nobody touched is submitted as exactly what is stored — by
 * construction rather than by the coincidence that the input was prefilled
 * with it. The difference shows up the moment a value fails to load, a
 * checkbox re-renders, or a browser fills something in.
 */
function useFields(stored: Record<string, unknown>) {
  const [changed, setChanged] = useState<Record<string, unknown>>({});

  const value = (name: string): string => {
    const overlay = changed[name];
    if (overlay !== undefined) return String(overlay);
    return String(stored[name] ?? '');
  };

  const checked = (name: string): boolean => {
    const overlay = changed[name];
    if (overlay !== undefined) return overlay === true;
    return stored[name] === true;
  };

  const set = (name: string, next: unknown) =>
    setChanged((current) => ({ ...current, [name]: next }));

  /** Stored, overlaid with what changed. Never a form read for these fields. */
  const merged = (): Record<string, unknown> => ({ ...stored, ...changed });

  const isSet = (name: string): boolean => value(name).trim() !== '';

  return { value, checked, set, merged, isSet, changed };
}

/**
 * Whether a document could actually be sealed.
 *
 * First on the page, because it is the one thing here that stops an envelope
 * being sendable at all — `distribute` refuses while it is missing (spec/04
 * §2), and finding that out from a rejected send is finding it out late.
 *
 * No form: this is deployment configuration, and a signing key does not
 * belong in a browser.
 */
function SigningSection({ token }: { token: string }): ReactElement {
  const [status, setStatus] = useState<SigningStatus | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    readSigningStatus(token)
      .then(setStatus)
      .catch((error: unknown) => setFailure(messageOf(error)));
  }, [token]);

  return (
    <section className="setting" aria-labelledby="signing-heading">
      <h2 id="signing-heading">Signing certificate</h2>
      {failure !== null ? (
        <div className="error" role="alert">
          <p>{failure}</p>
        </div>
      ) : status === null ? (
        <p className="muted">Checking…</p>
      ) : status.configured ? (
        <div className="notice" role="status">
          <p>A signing certificate is configured. Envelopes can be sealed.</p>
        </div>
      ) : (
        <div className="error" role="alert">
          <p>
            No envelope can be sent: {status.reason}
          </p>
          <p>
            This is deployment configuration rather than a setting — it is set
            on the server, not here. Sending is refused until it exists,
            deliberately: a signer who signs a document this instance cannot
            complete has made a declaration nothing can act on.
          </p>
        </div>
      )}
    </section>
  );
}

function StorageSection({
  token,
  view,
  onSaved,
}: {
  token: string;
  view: SettingView | undefined;
  onSaved: () => Promise<void>;
}): ReactElement {
  const stored = view?.value ?? {};
  const fields = useFields(stored);
  const { outcome, busy, handle } = useSettingForm(
    token,
    'storage',
    (form) => ({
      // The stored value with the changes laid over it: a field nobody edited
      // is sent as what is stored, not as whatever the form happens to hold.
      ...fields.merged(),
      // Present only when somebody asked to replace the credential — the
      // inputs do not exist otherwise (see `Credential`).
      ...secret(form, 'accessKeyId'),
      ...secret(form, 'secretAccessKey'),
    }),
    onSaved,
  );

  return (
    <Section
      id="storage"
      title="Object storage"
      view={view}
      note="DigitalOcean Spaces is S3-compatible: the endpoint is the regional one, for example https://nyc3.digitaloceanspaces.com. Until this is configured, uploads refuse with SETTING_MISSING and everything else keeps working."
    >
      <form onSubmit={handle} className="assent" aria-labelledby="storage-heading">
        <p>
          <label htmlFor="bucket">
            Bucket / Space name <SetMark set={fields.isSet('bucket')} />
          </label>
          <input
            id="bucket"
            name="bucket"
            type="text"
            value={fields.value('bucket')}
            onChange={(event) => fields.set('bucket', event.target.value)}
            required
          />
        </p>
        {/* Endpoint before region, because on every provider that names its
            host after its region the endpoint is the one you actually know —
            and typing it fills the other in. */}
        <p>
          <label htmlFor="endpoint">
            Endpoint <SetMark set={fields.isSet('endpoint')} unsetLabel="Not set (optional)" />
          </label>
          <input
            id="endpoint"
            name="endpoint"
            type="url"
            value={fields.value('endpoint')}
            placeholder="https://nyc3.digitaloceanspaces.com"
            onChange={(event) => {
              fields.set('endpoint', event.target.value);
              const implied = regionFromEndpoint(event.target.value);
              // Never overwrites something already there. A region typed by
              // hand is the one being signed with, and a host that merely
              // looks regional is not a reason to change it.
              if (implied !== null && fields.value('region').trim() === '') {
                fields.set('region', implied);
              }
            }}
          />
        </p>
        <p>
          <label htmlFor="region">
            Region <SetMark set={fields.isSet('region')} />
          </label>
          <input
            id="region"
            name="region"
            type="text"
            value={fields.value('region')}
            onChange={(event) => fields.set('region', event.target.value)}
            placeholder="nyc3"
            aria-describedby="region-help"
            required
          />
          <span id="region-help" className="muted">
            Filled in from the endpoint where the host names it. It is not a
            second copy of the endpoint: this is the region your requests are
            signed with, and it chooses no host.
          </span>
        </p>

        <Credential stored={view?.hasSecret === true} legend="Access key">
          <>
            <p>
              <label htmlFor="accessKeyId">Access key ID</label>
              <input id="accessKeyId" name="accessKeyId" type="password" autoComplete="off" />
            </p>
            <p>
              <label htmlFor="secretAccessKey">Secret access key</label>
              <input id="secretAccessKey" name="secretAccessKey" type="password" autoComplete="off" />
              <span className="muted">
                Both halves together or neither — a key without its secret cannot
                authenticate, and the failure would land at the first upload.
              </span>
            </p>
          </>
        </Credential>

        <p>
          <label htmlFor="forcePathStyle">
            <input
              id="forcePathStyle"
              name="forcePathStyle"
              type="checkbox"
              checked={fields.checked('forcePathStyle')}
              onChange={(event) => fields.set('forcePathStyle', event.target.checked)}
            />
            <span>Force path-style addressing (MinIO and some S3-compatible stores)</span>
          </label>
        </p>
        <Result outcome={outcome} />
        <Buttons busy={busy} />
      </form>
    </Section>
  );
}

function MailSection({
  token,
  view,
  onSaved,
}: {
  token: string;
  view: SettingView | undefined;
  onSaved: () => Promise<void>;
}): ReactElement {
  const stored = view?.value ?? {};
  const fields = useFields(stored);
  const { outcome, busy, handle } = useSettingForm(
    token,
    'mail',
    (form) => ({
      transport: 'smtp2go',
      ...fields.merged(),
      ...secret(form, 'apiKey'),
      // Only the test reads this; the write ignores it. Read from the form
      // rather than the field state because it is deliberately not part of
      // the configuration and must never reach `merged()`.
      ...(text(form, 'to') === '' ? {} : { to: text(form, 'to') }),
    }),
    onSaved,
  );

  return (
    <Section
      id="mail"
      title="Outbound mail (SMTP2GO)"
      view={view}
      note="Test sends a real message to the address below and reports what SMTP2GO said. A rejected key comes back inside a 200 with the failures counted, so a test that says nothing left is a test that failed."
    >
      <form onSubmit={handle} className="assent" aria-labelledby="mail-heading">
        <p>
          <label htmlFor="fromAddress">
            From address <SetMark set={fields.isSet('fromAddress')} />
          </label>
          <input
            id="fromAddress"
            name="fromAddress"
            type="email"
            value={fields.value('fromAddress')}
            onChange={(event) => fields.set('fromAddress', event.target.value)}
            required
          />
        </p>
        <p>
          <label htmlFor="fromName">
            From name <SetMark set={fields.isSet('fromName')} unsetLabel="Not set (optional)" />
          </label>
          <input
            id="fromName"
            name="fromName"
            type="text"
            value={fields.value('fromName')}
            onChange={(event) => fields.set('fromName', event.target.value)}
          />
        </p>

        <Credential stored={view?.hasSecret === true} legend="SMTP2GO API key">
          <p>
            <label htmlFor="apiKey">SMTP2GO API key</label>
            <input id="apiKey" name="apiKey" type="password" autoComplete="off" />
          </p>
        </Credential>

        <p>
          <label htmlFor="to">Send the test to</label>
          <input id="to" name="to" type="email" />
          <span className="muted">Only used by Test. Never saved.</span>
        </p>
        <Result outcome={outcome} />
        <Buttons busy={busy} />
      </form>
    </Section>
  );
}

function TimestampingSection({
  token,
  view,
  onSaved,
}: {
  token: string;
  view: SettingView | undefined;
  onSaved: () => Promise<void>;
}): ReactElement {
  const stored = Array.isArray(view?.value['authorities'])
    ? (view.value['authorities'] as string[])
    : [];
  const { outcome, busy, handle } = useSettingForm(
    token,
    'timestamping',
    (form) => ({
      authorities: String(form.get('authorities') ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''),
    }),
    onSaved,
  );

  return (
    <Section
      id="timestamping"
      title="Timestamp authorities"
      view={view}
      note="One URL per line, two to eight of them, tried in order. Timestamping fails hard and never falls back to system time (NN-05), so a single authority means one outage stops all sealing. The provisioned defaults are free public endpoints — fine for development, not something to rest legal weight on: this authority's clock becomes the time your signatures assert."
    >
      <form onSubmit={handle} className="assent" aria-labelledby="timestamping-heading">
        <p>
          <label htmlFor="authorities">Authority URLs</label>
          <textarea
            id="authorities"
            name="authorities"
            rows={4}
            defaultValue={stored.join('\n')}
            placeholder={'https://freetsa.org/tsr\nhttp://timestamp.digicert.com'}
            required
          />
        </p>
        <Result outcome={outcome} />
        <Buttons busy={busy} />
      </form>
    </Section>
  );
}
